import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { GripHorizontal } from 'lucide-react';

const VISIBLE_CELLS = 21;
const CELL_W = 36;
const CELL_H = 34;
const EDGE_THRESHOLD = 2;

/**
 * FloatingTape — TM tape visualization overlay.
 * Fixed tape with moving head highlight. Smooth edge-scrolling via FLIP animation.
 */
export function FloatingTape({ simResult, simStep, states, simInput }) {
  const panelRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const [pos, setPos] = useState(null);
  const [viewStart, setViewStart] = useState(0);
  const [offsetPx, setOffsetPx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const initRef = useRef(false);
  const justInitRef = useRef(false); // skip one edge-scroll cycle after init
  const prevHeadRef = useRef(null); // track head direction for edge-scroll

  const step = (simResult && simStep >= 0) ? simResult.path?.[simStep] : null;
  const tape = step?.tape;
  const headPos = step?.headPos ?? 0;
  const stateId = step?.state;

  const stateName = stateId ? (states.find(s => s.id === stateId)?.name || '?') : '?';
  const isFinished = step && simStep === simResult.path.length - 1 && simResult.status;

  // Smooth shift: FLIP technique
  const smoothShiftTo = useCallback((newStart) => {
    setViewStart(prev => {
      const delta = newStart - prev;
      if (delta === 0) return prev;
      // Set reverse offset so visual position stays the same, then animate to 0
      setOffsetPx(-delta * CELL_W);
      setAnimating(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setOffsetPx(0);
          setTimeout(() => setAnimating(false), 420);
        });
      });
      return newStart;
    });
  }, []);

  // Compute initial viewStart when simulation begins
  useEffect(() => {
    if (!simResult) {
      setPos(null);
      setViewStart(0);
      setOffsetPx(0);
      setAnimating(false);
      initRef.current = false;
      justInitRef.current = false;
      prevHeadRef.current = null;
      return;
    }
    const inputLen = (simInput || '').length;
    let initial;
    if (inputLen <= VISIBLE_CELLS) {
      // Center the input string: cell 0 is left-of-center
      initial = 0 - Math.floor((VISIBLE_CELLS - inputLen) / 2);
    } else {
      // Show cell -1 then fill from 0
      initial = -1;
    }
    setViewStart(initial);
    setOffsetPx(0);
    setAnimating(false);
    initRef.current = true;
    justInitRef.current = true;
  }, [simResult, simInput]);

  // Edge-scrolling: smooth shift when head nears edges (direction-aware)
  useEffect(() => {
    if (!step || !initRef.current) return;
    if (justInitRef.current) {
      justInitRef.current = false;
      prevHeadRef.current = headPos;
      return;
    }

    const prev = prevHeadRef.current;
    prevHeadRef.current = headPos;
    if (prev === null) return;

    const viewEnd = viewStart + VISIBLE_CELLS - 1;
    const movingLeft = headPos < prev;
    const movingRight = headPos > prev;

    if (movingLeft && headPos <= viewStart + EDGE_THRESHOLD) {
      smoothShiftTo(headPos - Math.floor(VISIBLE_CELLS / 2));
    } else if (movingRight && headPos >= viewEnd - EDGE_THRESHOLD) {
      smoothShiftTo(headPos - Math.floor(VISIBLE_CELLS / 2));
    }
  }, [headPos, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const cells = useMemo(() => {
    if (!tape) return [];
    // Render extra cells to cover animation buffer
    const buffer = 12;
    const renderStart = viewStart - buffer;
    const renderCount = VISIBLE_CELLS + buffer * 2;
    const result = [];
    for (let i = 0; i < renderCount; i++) {
      const p = renderStart + i;
      const sym = tape?.get?.(p) ?? (typeof tape === 'object' && !tape?.get ? tape[p] : undefined) ?? '\u25A1';
      result.push({ pos: p, sym, isHead: p === headPos });
    }
    return result;
  }, [tape, viewStart, headPos]);

  // The visible window clips to VISIBLE_CELLS width
  const clipWidth = VISIBLE_CELLS * CELL_W + 20; // +20 for gap-px spacing
  const bufferOffset = 12 * CELL_W; // translateX to show correct window

  // --- Drag logic ---
  const onPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const parentRect = panel.offsetParent?.getBoundingClientRect() || { left: 0, top: 0 };
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left - parentRect.left,
      origY: rect.top - parentRect.top,
    };
    const onMove = (me) => {
      if (!dragState.current.dragging) return;
      const dx = me.clientX - dragState.current.startX;
      const dy = me.clientY - dragState.current.startY;
      setPos({ x: dragState.current.origX + dx, y: dragState.current.origY + dy });
    };
    const onUp = () => {
      dragState.current.dragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, []);

  if (!step || !simResult || simStep < 0) return null;

  const positionStyle = pos
    ? { left: pos.x, top: pos.y, transform: 'none' }
    : { left: '50%', top: 12, transform: 'translateX(-50%)' };

  return (
    <div
      ref={panelRef}
      className="absolute z-30 pointer-events-auto select-none"
      data-testid="floating-tape"
      style={{ ...positionStyle, maxWidth: 'calc(100% - 24px)' }}
    >
      <div className="rounded-xl border-2 border-foreground/20 bg-card shadow-xl shadow-black/15 overflow-hidden">
        {/* Drag handle header */}
        <div
          className="flex items-center justify-between px-3 py-1.5 cursor-grab active:cursor-grabbing bg-muted/60 border-b border-border/40"
          onPointerDown={onPointerDown}
          data-testid="tape-drag-handle"
        >
          <div className="flex items-center gap-2.5">
            <GripHorizontal className="w-3.5 h-3.5 text-muted-foreground/60" />
            <span className="text-[11px] font-semibold text-foreground/70 tracking-wide uppercase">
              Tape
            </span>
            <span className="text-[10px] font-mono text-muted-foreground bg-muted/80 px-1.5 py-0.5 rounded">
              Step {simStep} / {simResult.path ? simResult.path.length - 1 : '?'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-foreground/80">
              <span className="text-muted-foreground">State:</span> <span className="font-bold">{stateName}</span>
            </span>
            <span className="text-[10px] font-mono text-muted-foreground">
              Head: {headPos}
            </span>
            {isFinished && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                simResult.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-600'
                : simResult.status === 'rejected' ? 'bg-red-500/20 text-red-600'
                : simResult.status === 'loop' ? 'bg-amber-500/20 text-amber-600'
                : 'bg-orange-500/20 text-orange-600'
              }`}>
                {simResult.status === 'loop' ? 'STEP LIMIT' : simResult.status.toUpperCase()}
              </span>
            )}
          </div>
        </div>

        {/* Tape strip — clipped to visible width */}
        <div className="px-2 py-2.5 bg-card">
          <div className="flex items-center">
            <span className="text-sm text-muted-foreground/40 px-1.5 select-none font-bold">&hellip;</span>

            <div style={{ width: clipWidth, overflow: 'hidden' }}>
              <div
                className="flex gap-px"
                style={{
                  transform: `translateX(${-bufferOffset + offsetPx}px)`,
                  transition: animating ? 'transform 400ms ease-out' : 'none',
                }}
              >
                {cells.map(({ pos: cellPos, sym, isHead }) => (
                  <div
                    key={cellPos}
                    data-head={isHead ? 'true' : undefined}
                    className="flex flex-col items-center shrink-0"
                    style={{ width: CELL_W }}
                  >
                    <div style={{ height: 16 }} className="flex items-center justify-center">
                      {isHead && (
                        <div className="text-primary text-[10px] font-black select-none drop-shadow-sm" data-testid="tape-head-indicator">
                          &#9660;
                        </div>
                      )}
                    </div>

                    <div
                      className={`flex items-center justify-center border-2 text-sm font-mono font-semibold transition-all duration-200 ${
                        isHead
                          ? 'bg-primary/20 border-primary text-primary ring-2 ring-primary/30 shadow-md shadow-primary/25 scale-105'
                          : sym === '\u25A1'
                          ? 'bg-muted/30 border-border/50 text-muted-foreground/35'
                          : 'bg-muted/50 border-border/70 text-foreground'
                      }`}
                      style={{ width: CELL_W - 2, height: CELL_H, borderRadius: 6 }}
                      data-testid={`tape-cell-${cellPos}`}
                    >
                      {sym}
                    </div>

                    <span className={`text-[8px] mt-0.5 select-none font-mono ${
                      isHead ? 'text-primary font-bold' : 'text-muted-foreground/30'
                    }`}>
                      {cellPos}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <span className="text-sm text-muted-foreground/40 px-1.5 select-none font-bold">&hellip;</span>
          </div>
        </div>
      </div>
    </div>
  );
}
